const sectionsToRender = [
  {
    id: '#cart-counter',
    section: 'cart-counter',
    selector: '#shopify-section-cart-counter'
  },
  {
    id: '#CartDrawer-Body',
    section: 'cart-drawer',
    selector: '#shopify-section-cart-drawer #CartDrawer-Body'
  }
];

class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener(
      'keyup',
      event => event.code.toUpperCase() === 'ESCAPE' && this.close()
    );
    this.querySelector('#CartDrawer-Overlay').addEventListener(
      'click',
      this.close.bind(this)
    );
    this.setCartLink();
    this.observeBodyRerenders();
    /* document-level Escape while open · the element-level keyup above only fires
       once focus is inside the drawer; this one works from the first frame. */
    this._escHandler = e => { if (e.code && e.code.toUpperCase() === 'ESCAPE') this.close(); };
    this.parentElement.addEventListener(
      'shopify:section:select',
      () => this.open()
    );
    this.parentElement.addEventListener(
      'shopify:section:deselect',
      () => this.close()
    );
  }

  setCartLink() {
    const cartLink = document.querySelector('[data-cart-link]');
    cartLink?.setAttribute('role', 'button');
    cartLink?.setAttribute('aria-haspopup', 'dialog');
    cartLink?.addEventListener('click', event => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink?.addEventListener('keydown', event => {
      if (event.code.toUpperCase() !== 'SPACE') return;
      event.preventDefault();
      this.open(cartLink);
    });
  }

  open(opener) {
    if (opener) this.setActiveElement(opener);
    /* AJAX add-to-cart path passes no opener · remember whatever had focus (the
       Add to Bag button) so close() can return focus there. 2026-07-22 P1 a11y. */
    else if (!this.activeElement && document.activeElement !== document.body) {
      this.setActiveElement(document.activeElement);
    }
    this.classList.add('is-visible');
    this.querySelector('.cart-drawer__inner').setAttribute('aria-hidden', 'false');

    /* Focus + trap · the old transitionend hook never fired (no transition on
       the element) and focusOnCartDrawer() targeted a div with no tabindex, so
       focus never entered the dialog. trapFocus is the theme's own utility
       (global.js) · removeTrapFocus was already wired in close(). Focus is
       retried through the open transition: while the inner is still
       visibility:hidden, focus() is silently refused. */
    this.setBackgroundInert(true);
    document.addEventListener('keyup', this._escHandler);
    this._focusAttempts = 0;
    requestAnimationFrame(() => this._focusIntoDrawer());

    document.body.classList.add('overflow-hidden');
  }

  _focusIntoDrawer() {
    if (!this.classList.contains('is-visible')) return;
    const inner = this.querySelector('.cart-drawer__inner');
    if (!inner) return;
    const closeBtn = this.querySelector('[data-drawer-close]');
    trapFocus(inner, closeBtn || inner);
    const ae = document.activeElement;
    if ((!ae || !this.contains(ae)) && ++this._focusAttempts < 8) {
      setTimeout(() => this._focusIntoDrawer(), 120);
    }
  }

  close() {
    document.removeEventListener('keyup', this._escHandler);
    this.classList.remove('is-visible');
    this.querySelector('.cart-drawer__inner').setAttribute('aria-hidden', 'true');
    this.setBackgroundInert(false);
    removeTrapFocus(this.activeElement);
    this.setActiveElement(null);

    const isHeaderMenuOpen = header.classList.contains('menu-open');

    if (isHeaderMenuOpen) {
      return;
    }

    document.body.classList.remove('overflow-hidden');

    // if we are on the cart page, resubmit form
    if (window.location.pathname === '/cart') {
      const cartDrawerForm = document.getElementById('CartDrawer-FormSummary');
      if (cartDrawerForm) {
        cartDrawerForm.submit();
      }
    }
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  renderContents(response, open = true) {
    this.getSectionsToRender()?.forEach(section => {
      const sectionElement = document.querySelector(section.id);
      sectionElement.innerHTML = this.getSectionInnerHTML(
        response.sections[section.section],
        section.selector
      );
    });
    if (!open) {
      return;
    }

    this.open();
  }

  getSectionsToRender() {
    return sectionsToRender;
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector).innerHTML;
  }

  focusOnCartDrawer() {
    const cartDrawer = this.querySelector('.cart-drawer');
    if (cartDrawer) {
      // cartDrawer.setAttribute('tabindex', '0');
      cartDrawer.focus();
    }
  }

  /* Background must not stay keyboard/SR-interactive behind the modal drawer.
     Everything at body level that doesn't contain the drawer goes inert while
     open; the exact list is remembered so repeated open/close cycles stay
     symmetric. 2026-07-22 P1 a11y. */
  setBackgroundInert(on) {
    const section = this.closest('.shopify-section') || this;
    if (on) {
      this._inertedEls = Array.from(document.body.children).filter(el =>
        !el.contains(section) &&
        !['SCRIPT', 'STYLE', 'LINK', 'TEMPLATE'].includes(el.tagName) &&
        !el.inert
      );
      this._inertedEls.forEach(el => { el.inert = true; });
    } else {
      (this._inertedEls || []).forEach(el => { el.inert = false; });
      this._inertedEls = [];
    }
  }

  /* The drawer body re-renders via innerHTML swaps (qty / remove / add). That
     destroys the trap's captured first/last nodes and can kill the focused
     element. Re-arm the trap after each swap, restoring focus to the same
     control (matched by name+aria-label) when it survived the morph. */
  observeBodyRerenders() {
    const body = this.querySelector('[data-cart-body]');
    if (!body || this._bodyObserver) return;
    this._bodyObserver = new MutationObserver(() => {
      if (!this.classList.contains('is-visible')) return;
      clearTimeout(this._retrapT);
      this._retrapT = setTimeout(() => {
        const inner = this.querySelector('.cart-drawer__inner');
        if (!inner) return;
        const prev = this._lastFocusSig;
        let target = null;
        if (prev) {
          target = inner.querySelector(prev) || null;
        }
        trapFocus(inner, target || this.querySelector('[data-drawer-close]') || inner);
      }, 120);
    });
    this._bodyObserver.observe(body, { childList: true, subtree: true });
    /* remember a re-findable signature of the focused control */
    this.addEventListener('focusin', e => {
      const t = e.target;
      if (!t || !t.getAttribute) return;
      if (t.name) this._lastFocusSig = `[name="${t.name}"]`;
      else if (t.getAttribute('aria-label')) this._lastFocusSig = `[aria-label="${CSS.escape ? CSS.escape(t.getAttribute('aria-label')) : t.getAttribute('aria-label')}"]`;
      else this._lastFocusSig = null;
    });
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return sectionsToRender;
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
